import {
  type HRContractType,
  type HRCreateContractBody,
  type HREmployee,
  type HRPosition,
  HttpError,
  api,
} from '@/api/client'
import { FormField } from '@/components/shared/FormField'
import { PageHeader } from '@/components/shared/PageHeader'
import { RequirePermission } from '@/components/shared/RequirePermission'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { useToast } from '@/components/ui/toast'
import { useIsReadOnly } from '@/hooks/useSubscription'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, createRoute, useNavigate, useSearch } from '@tanstack/react-router'
import { ArrowLeft, Send } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Route as appLayoutRoute } from '../../layout'

interface Search {
  employee_id?: string
}

export const Route = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/hr/contracts/new',
  component: NewContractPage,
  validateSearch: (search: Record<string, unknown>): Search => ({
    employee_id: typeof search.employee_id === 'string' ? search.employee_id : undefined,
  }),
})

const CONTRACT_TYPES: HRContractType[] = ['cdi', 'cdd', 'stage', 'apprenticeship', 'interim']

// Salaries are XOF centimes on the wire (1 XOF = 100 cents).
function xofToCents(xof: string): number {
  const n = Number(xof)
  if (!xof || Number.isNaN(n)) return 0
  return Math.round(n * 100)
}

function NewContractPage() {
  const { t } = useTranslation('hr')
  // L7b-bis
  const readOnly = useIsReadOnly()
  const navigate = useNavigate()
  const toast = useToast()
  const qc = useQueryClient()
  const search = useSearch({ from: Route.id })

  const [employeeId, setEmployeeId] = useState(search.employee_id ?? '')
  const [contractType, setContractType] = useState<HRContractType>('cdi')
  const [startDate, setStartDate] = useState(() => new Date().toISOString().slice(0, 10))
  const [endDate, setEndDate] = useState('')
  const [trialPeriodEnd, setTrialPeriodEnd] = useState('')
  const [positionId, setPositionId] = useState('')
  const [positionTitle, setPositionTitle] = useState('')
  const [departmentId, setDepartmentId] = useState('')
  const [baseSalary, setBaseSalary] = useState('')
  const [sursalaire, setSursalaire] = useState('')
  const [transport, setTransport] = useState('')
  const [housing, setHousing] = useState('')
  const [otherAllowances, setOtherAllowances] = useState('')
  const [otherDesc, setOtherDesc] = useState('')
  const [weeklyHours, setWeeklyHours] = useState('40')
  const [daysPerWeek, setDaysPerWeek] = useState('5')
  const [nonCompete, setNonCompete] = useState(false)
  const [confidentiality, setConfidentiality] = useState(true)
  const [notes, setNotes] = useState('')

  const employeesQuery = useQuery({
    queryKey: ['hr', 'employees', 'active-for-pick'],
    queryFn: () => api.hr.listEmployees({ status: 'active', limit: 500 }),
  })
  const positionsQuery = useQuery({
    queryKey: ['hr', 'positions'],
    queryFn: () => api.hr.listPositions(),
  })
  const departmentsQuery = useQuery({
    queryKey: ['hr', 'departments'],
    queryFn: () => api.hr.listDepartments(),
  })

  const selectedEmployee: HREmployee | undefined = useMemo(
    () => employeesQuery.data?.data.find((e) => e.id === employeeId),
    [employeesQuery.data, employeeId],
  )
  const selectedPosition: HRPosition | undefined = useMemo(
    () => positionsQuery.data?.find((p) => p.id === positionId),
    [positionsQuery.data, positionId],
  )

  // When the user picks a position, pre-fill title + department + salary
  // range. baseSalary is intentionally not a dependency — the effect only
  // seeds it when empty, so re-running on every keystroke would clobber
  // manual edits.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional
  useEffect(() => {
    if (!selectedPosition) return
    setPositionTitle(selectedPosition.title)
    if (selectedPosition.department_id) setDepartmentId(selectedPosition.department_id)
    if (selectedPosition.min_salary != null && !baseSalary) {
      setBaseSalary(String(Math.round(selectedPosition.min_salary / 100)))
    }
  }, [selectedPosition])

  const mutation = useMutation({
    mutationFn: (body: HRCreateContractBody) => api.hr.createContract(body),
    onSuccess: async (created) => {
      await qc.invalidateQueries({ queryKey: ['hr'] })
      toast.show({
        variant: 'success',
        title: t('contracts.new'),
        description: created.contract_number,
      })
      await navigate({
        to: '/hr/employees/$employeeId',
        params: { employeeId: created.employee_id },
      })
    },
    onError: (err) => {
      const code = err instanceof HttpError ? err.code : null
      const key = code ? `errors.${code}` : null
      const fallback = err instanceof HttpError ? err.message : String(err)
      toast.show({
        variant: 'error',
        title: t('contracts.new'),
        description: key ? t(key, { defaultValue: fallback }) : fallback,
      })
    },
  })

  const isCDD = contractType === 'cdd'
  const missingRequired =
    !employeeId || !startDate || !positionTitle.trim() || !baseSalary || (isCDD && !endDate)

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (missingRequired) return
    const body: HRCreateContractBody = {
      employee_id: employeeId,
      contract_type: contractType,
      start_date: startDate,
      end_date: endDate || undefined,
      trial_period_end: trialPeriodEnd || undefined,
      base_salary: xofToCents(baseSalary),
      sursalaire: xofToCents(sursalaire),
      currency: 'XOF',
      transport_allowance: xofToCents(transport),
      housing_allowance: xofToCents(housing),
      other_allowances: xofToCents(otherAllowances),
      other_allowances_description: otherDesc || undefined,
      weekly_hours: Number(weeklyHours) || 40,
      days_per_week: Number(daysPerWeek) || 5,
      position_id: positionId || undefined,
      position_title: positionTitle,
      department_id: departmentId || undefined,
      non_compete_clause: nonCompete,
      confidentiality_clause: confidentiality,
      notes: notes || undefined,
    }
    return mutation.mutateAsync(body)
  }

  return (
    <RequirePermission permission="contracts:manage">
      <div className="mx-auto flex max-w-3xl flex-col gap-6">
        <Link
          to="/hr/contracts"
          className="inline-flex w-fit items-center gap-1.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          {t('contracts.title')}
        </Link>
        <PageHeader
          title={t('contracts.new')}
          description={
            selectedEmployee
              ? `${selectedEmployee.first_name} ${selectedEmployee.last_name} · ${selectedEmployee.employee_code}`
              : undefined
          }
        />

        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          {/* Parties */}
          <Card>
            <CardContent className="flex flex-col gap-4 p-5">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t('employees.title')}
              </p>
              <FormField label={t('employees.title')} required>
                <Select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}>
                  <option value="">—</option>
                  {employeesQuery.data?.data.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.first_name} {e.last_name} · {e.employee_code}
                    </option>
                  ))}
                </Select>
              </FormField>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <FormField label={t('contracts.type')} required>
                  <Select
                    value={contractType}
                    onChange={(e) => setContractType(e.target.value as HRContractType)}
                  >
                    {CONTRACT_TYPES.map((tp) => (
                      <option key={tp} value={tp}>
                        {t(`contract_type.${tp}`)}
                      </option>
                    ))}
                  </Select>
                </FormField>
                <FormField label={t('contracts.start_date')} required>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </FormField>
                <FormField
                  label={t('contracts.end_date')}
                  required={isCDD}
                  hint={isCDD ? 'Requis pour un CDD' : undefined}
                >
                  <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                </FormField>
                <FormField label={t('contracts.trial_period_end')}>
                  <Input
                    type="date"
                    value={trialPeriodEnd}
                    onChange={(e) => setTrialPeriodEnd(e.target.value)}
                  />
                </FormField>
              </div>
            </CardContent>
          </Card>

          {/* Poste */}
          <Card>
            <CardContent className="flex flex-col gap-4 p-5">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t('positions.title')}
              </p>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <FormField label={t('positions.position_title')} required>
                  <Input
                    value={positionTitle}
                    onChange={(e) => setPositionTitle(e.target.value)}
                    placeholder="ex. Agent de dédouanement"
                  />
                </FormField>
                <FormField
                  label={t('employees.position')}
                  hint="Pré-remplit le salaire et le département"
                >
                  <Select value={positionId} onChange={(e) => setPositionId(e.target.value)}>
                    <option value="">—</option>
                    {positionsQuery.data?.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.title}
                      </option>
                    ))}
                  </Select>
                </FormField>
                <FormField label={t('employees.department')} className="md:col-span-2">
                  <Select value={departmentId} onChange={(e) => setDepartmentId(e.target.value)}>
                    <option value="">—</option>
                    {departmentsQuery.data?.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                      </option>
                    ))}
                  </Select>
                </FormField>
              </div>
            </CardContent>
          </Card>

          {/* Rémunération */}
          <Card>
            <CardContent className="flex flex-col gap-4 p-5">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t('contracts.base_salary')}
              </p>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <FormField
                  label={t('contracts.base_salary')}
                  required
                  hint={t('contracts.base_salary_hint', {
                    defaultValue: 'Salaire de base issu du barème (XOF / mois)',
                  })}
                >
                  <Input
                    type="number"
                    min={0}
                    value={baseSalary}
                    onChange={(e) => setBaseSalary(e.target.value)}
                    placeholder="ex. 350000"
                  />
                </FormField>
                <FormField
                  label={t('contracts.sursalaire', { defaultValue: 'Sursalaire' })}
                  hint={t('contracts.sursalaire_hint', {
                    defaultValue: 'Prime négociée au-delà du barème (XOF / mois)',
                  })}
                >
                  <Input
                    type="number"
                    min={0}
                    value={sursalaire}
                    onChange={(e) => setSursalaire(e.target.value)}
                    placeholder="ex. 50000"
                  />
                </FormField>
                {(Number(baseSalary) > 0 || Number(sursalaire) > 0) && (
                  <div className="md:col-span-2 rounded-lg border border-border bg-muted/50 px-4 py-2 text-sm">
                    <span className="text-muted-foreground">
                      {t('contracts.gross_salary_preview', { defaultValue: 'Salaire brut' })} =
                    </span>{' '}
                    <span className="font-mono font-semibold tabular-nums">
                      {(Number(baseSalary || 0) + Number(sursalaire || 0)).toLocaleString('fr-FR')} XOF
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {' '}
                      ({t('contracts.base_salary')} + {t('contracts.sursalaire', { defaultValue: 'Sursalaire' })})
                    </span>
                  </div>
                )}
                <FormField label={t('contracts.transport_allowance')} hint="XOF / mois">
                  <Input
                    type="number"
                    min={0}
                    value={transport}
                    onChange={(e) => setTransport(e.target.value)}
                  />
                </FormField>
                <FormField label={t('contracts.housing_allowance')} hint="XOF / mois">
                  <Input
                    type="number"
                    min={0}
                    value={housing}
                    onChange={(e) => setHousing(e.target.value)}
                  />
                </FormField>
                <FormField label={t('contracts.other_allowances')} hint="XOF / mois">
                  <Input
                    type="number"
                    min={0}
                    value={otherAllowances}
                    onChange={(e) => setOtherAllowances(e.target.value)}
                  />
                </FormField>
                {Number(otherAllowances) > 0 ? (
                  <FormField label="Description" className="md:col-span-2">
                    <Input
                      value={otherDesc}
                      onChange={(e) => setOtherDesc(e.target.value)}
                      placeholder="ex. prime de responsabilité"
                    />
                  </FormField>
                ) : null}
              </div>
            </CardContent>
          </Card>

          {/* Temps de travail & clauses */}
          <Card>
            <CardContent className="flex flex-col gap-4 p-5">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t('contracts.weekly_hours')}
              </p>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <FormField label={t('contracts.weekly_hours')}>
                  <Input
                    type="number"
                    min={1}
                    step="0.5"
                    value={weeklyHours}
                    onChange={(e) => setWeeklyHours(e.target.value)}
                  />
                </FormField>
                <FormField label={t('contracts.days_per_week')}>
                  <Input
                    type="number"
                    min={1}
                    max={7}
                    value={daysPerWeek}
                    onChange={(e) => setDaysPerWeek(e.target.value)}
                  />
                </FormField>
              </div>
              <div className="grid grid-cols-1 gap-2 text-sm md:grid-cols-2">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={confidentiality}
                    onChange={(e) => setConfidentiality(e.target.checked)}
                    className="h-4 w-4 rounded border-border"
                  />
                  {t('contracts.confidentiality')}
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={nonCompete}
                    onChange={(e) => setNonCompete(e.target.checked)}
                    className="h-4 w-4 rounded border-border"
                  />
                  {t('contracts.non_compete')}
                </label>
              </div>
              <FormField label="Notes">
                <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
              </FormField>
            </CardContent>
          </Card>

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate({ to: '/hr/contracts' })}
            >
              {t('leaves.cancel')}
            </Button>
            <Button
              type="submit"
              loading={mutation.isPending}
              disabled={missingRequired || readOnly}
              title={
                readOnly
                  ? 'Compte en lecture seule — régularisez votre abonnement.'
                  : undefined
              }
            >
              <Send className="h-4 w-4" />
              {t('contracts.new')}
            </Button>
          </div>
        </form>
      </div>
    </RequirePermission>
  )
}
